import { useState } from 'react';
import { Badge, Button, ConfirmDialog, FileUpload, Input, SegmentedControl, Textarea, useToast } from '@dreamtree-org/twreact-ui';
import { SectionCard, QueryState } from '@/components';
import { errorText } from '@/lib/format';
import { uploadFile } from '@/lib/uploads';
import { useQuery, useMutation } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import { photoBadge, canReview, filesByKind, fileKind, reviewToast, countsLine, uploadProblem, UNIT_FILE_KINDS, MODEL_FILE_KINDS, MAX_ALT } from '../lib/photos';

/**
 * The records whose files this gallery shows (a registry: a new owner is an entry here), each with
 * its kinds' copy (lib/photos), its list and review calls (api/merchant) and the permissions that
 * offer the upload and review controls. The server checks every permission and rule again.
 *   inventory_unit  a stock unit (MERCH-59, MERCH-77): photos, which count, and illustrative images
 *   product_model   a product model (MERCH-81, D95): catalog images, never counted, always labelled
 */
const OWNERS = {
  inventory_unit: { title: 'Photos', kinds: UNIT_FILE_KINDS, list: 'unitPhotos', review: 'reviewUnitPhoto', idKey: 'unit_id', upload: ['inventory', 'update'], decide: ['inventory', 'inspect'] },
  product_model: { title: 'Catalog images', kinds: MODEL_FILE_KINDS, list: 'modelImages', review: 'reviewModelImage', idKey: 'product_model_id', upload: ['catalog', 'update'], decide: ['catalog', 'publish'] },
};

/**
 * A record's files in gallery order, one section per kind. An assistant's upload waits as
 * "MCP upload — pending review": a person with the owner's review permission approves or rejects
 * it. Staff with its upload permission add files here; the kind is picked before the upload. Copy
 * comes from the owner's kinds and each kind's rules (cap, alt text) from the server's `kinds`.
 * The server enforces every rule; this only offers the controls.
 */
export default function MediaGallery({ ownerType, ownerId }) {
  const owner = OWNERS[ownerType];
  const keys = Object.keys(owner.kinds);
  const can = useCan();
  const { toast } = useToast();
  const q = useQuery(owner.list, { [owner.idKey]: ownerId }, { select: (r) => r.data, enabled: !!ownerId });
  const review = useMutation(owner.review);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');

  const decide = async (file, decision, why) => {
    try {
      await review.run({ media_id: file.id, decision, reason: why });
      const t = reviewToast(file, decision, owner.kinds);
      toast.success(t.title, t.body);
      setRejecting(null);
      setReason('');
      q.reload();
    } catch (ex) {
      toast.error('Could not save the review', errorText(ex));
    }
  };

  const fileList = (files) => (
    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {files.map((p) => {
        const badge = photoBadge(p);
        return (
          <li key={p.id} className="space-y-1">
            <img src={p.url} alt={p.alt || `${fileKind(p, owner.kinds).noun} ${p.id}`} className="aspect-square w-full object-cover rounded-md border border-[var(--app-border)]" />
            {badge ? <Badge variant={badge.variant} size="sm">{badge.label}</Badge> : null}
            {canReview(p, can, owner.decide) ? (
              <div className="flex gap-1">
                <Button size="sm" loading={review.loading} onClick={() => decide(p, 'approve')}>Approve</Button>
                <Button size="sm" variant="outline" disabled={review.loading} onClick={() => setRejecting(p)}>Reject</Button>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );

  return (
    <SectionCard title={owner.title}>
      <QueryState query={q}>
        {(d) => {
          const files = (d && d.files) || [];
          const byKind = filesByKind(files, owner.kinds);
          const counts = (d && d.counts) || {};
          const rules = (d && d.kinds) || {};
          return (
            <div className="space-y-4">
              {keys.map((k) => (
                <section key={k} className="space-y-2" aria-label={owner.kinds[k].title(rules[k])}>
                  <h4 className="text-sm font-semibold">{owner.kinds[k].title(rules[k])}</h4>
                  {byKind[k].length ? (
                    <>
                      <p className="text-sm opacity-70">{countsLine(counts[k])}</p>
                      {fileList(byKind[k])}
                    </>
                  ) : (
                    <p className="text-sm opacity-70">{owner.kinds[k].empty}</p>
                  )}
                </section>
              ))}
              {can(owner.upload[0], owner.upload[1]) ? <ImageUpload ownerType={ownerType} ownerId={ownerId} byKind={byKind} rules={rules} onDone={() => q.reload()} /> : null}
            </div>
          );
        }}
      </QueryState>
      <ConfirmDialog
        isOpen={!!rejecting}
        title={fileKind(rejecting, owner.kinds).rejectTitle}
        description={
          <div className="space-y-2">
            <p>It stays hidden from shoppers and does not count toward publishing.</p>
            <Textarea label="Reason (optional)" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          </div>
        }
        confirmLabel="Reject"
        destructive
        loading={review.loading}
        onConfirm={() => decide(rejecting, 'reject', reason.trim() || undefined)}
        onCancel={() => {
          setRejecting(null);
          setReason('');
        }}
      />
    </SectionCard>
  );
}

/** Add one image: pick the kind first (when the owner has several), then the file and its alt text. */
function ImageUpload({ ownerType, ownerId, byKind, rules, onDone }) {
  const kinds = OWNERS[ownerType].kinds;
  const keys = Object.keys(kinds);
  const { toast } = useToast();
  const [kind, setKind] = useState(keys[0]);
  const [files, setFiles] = useState([]);
  const [alt, setAlt] = useState('');
  const [busy, setBusy] = useState(false);
  const k = kinds[kind];
  const problem = files.length ? uploadProblem(kind, { count: files.length, existing: byKind[kind].length, alt, rules: rules[kind] }, kinds) : '';

  const send = async () => {
    setBusy(true);
    try {
      for (const file of files) await uploadFile(file, { ownerType, ownerId, kind, alt: alt.trim() || undefined });
      toast.success(`${k.noun} added`, k.added);
      setFiles([]);
      setAlt('');
      onDone();
    } catch (ex) {
      toast.error('Could not upload', errorText(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-2 border-t border-[var(--app-border)] pt-3" aria-label="Add an image">
      {keys.length > 1 ? (
        <SegmentedControl
          aria-label="Kind of image"
          size="sm"
          value={kind}
          onChange={(v) => setKind(String(v))}
          options={keys.map((key) => ({ value: key, label: kinds[key].option }))}
        />
      ) : null}
      <p className="text-xs opacity-70">{k.hint}</p>
      <FileUpload accept="image/jpeg,image/png,image/webp" multiple={k.multiple} maxSize={8 * 1024 * 1024} files={files} onFileSelect={(f) => setFiles(f)} onFileRemove={(i) => setFiles(files.filter((_, j) => j !== i))} />
      <Input
        label={k.altLabel}
        required={Boolean(rules[kind] ? rules[kind].alt_required : k.altRequired)}
        maxLength={MAX_ALT}
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        error={problem || undefined}
      />
      <Button size="sm" loading={busy} disabled={!files.length || !!problem} onClick={send}>Upload</Button>
    </section>
  );
}
